<template>
  <div class="mp-page">

    <!-- HERO -->
    <section class="mp-hero">
      <div class="mp-hero-inner">
        <div class="mp-hero-text">
          <p class="mp-eyebrow">Programs</p>
          <h1 class="mp-h1">NexaScale Mentorship.</h1>
          <p class="mp-hero-body">
            A free 4-month mentorship program for entry-level engineers. We started with 
            Cloud Engineering and we've grown with the industry — today our focus is 
            squarely on AI readiness, applied ML, and the skills that actually matter 
            in the jobs being created right now. Cloud, DevOps, and infrastructure 
            still feature, but AI is where we're going.
          </p>
          <a class="mp-btn" href="https://forms.gle/nivXcK3MwJ8ZeFRp7" target="_blank">
            Stay in touch →
          </a>
        </div>
        <div class="mp-hero-image">
          <img src="../../assets/images/programs/cloud-mentorship-min.jpg" alt="NexaScale Mentorship" />
        </div>
      </div>
    </section>

    <!-- CONTENT -->
    <section class="mp-content">
      <div class="mp-content-inner">

        <div class="mp-section">
          <h2 class="mp-section-title">Our objective</h2>
          <div class="mp-section-body">
            <p>
              The industry shifted. We shifted with it. A few years ago, the highest-demand 
              entry point into tech was Cloud Engineering — and we built a program around 
              that. Now, the most urgent skills are in AI: building with LLMs, understanding 
              ML pipelines, shipping AI-powered products, and knowing how to apply these 
              tools to real engineering problems.
            </p>
            <p>
              Our objective is the same as it's always been — prepare entry-level engineers 
              for the industry as it actually exists, not as it existed three years ago. 
              That means giving mentees hands-on experience with the tools, concepts, and 
              workflows they'll encounter on day one of a real role.
            </p>
            <p>
              We also connect program graduates to open-source contribution opportunities, 
              internships, and partner companies actively looking for junior talent.
            </p>
          </div>
        </div>

        <div class="mp-section">
          <h2 class="mp-section-title">What we cover</h2>
          <div class="mp-section-body">
            <p>
              Our curriculum evolves every cohort to stay current. Right now, sessions 
              are focused on the following areas — with cloud and infrastructure woven 
              in where relevant.
            </p>
            <div class="mp-topics">
              <div class="mp-topic">
                <span class="mp-topic-dot"></span>
                <div>
                  <span class="mp-topic-name">Applied AI &amp; LLM Engineering</span>
                  <span class="mp-topic-note">Building real products with large language models, prompt engineering, RAG pipelines</span>
                </div>
              </div>
              <div class="mp-topic">
                <span class="mp-topic-dot"></span>
                <div>
                  <span class="mp-topic-name">ML Engineering</span>
                  <span class="mp-topic-note">Model training, evaluation, deployment, and MLOps fundamentals</span>
                </div>
              </div>
              <div class="mp-topic">
                <span class="mp-topic-dot"></span>
                <div>
                  <span class="mp-topic-name">Vibe Coding &amp; AI-assisted Development</span>
                  <span class="mp-topic-note">Shipping faster with AI tools — Copilot, Cursor, Claude, and beyond</span>
                </div>
              </div>
              <div class="mp-topic">
                <span class="mp-topic-dot"></span>
                <div>
                  <span class="mp-topic-name">Cloud Engineering</span>
                  <span class="mp-topic-note">AWS, Azure, GCP — infrastructure, security, and cloud-native patterns</span>
                </div>
              </div>
              <div class="mp-topic">
                <span class="mp-topic-dot"></span>
                <div>
                  <span class="mp-topic-name">AI Security &amp; Responsible AI</span>
                  <span class="mp-topic-note">Understanding risks, bias, and safety in AI systems</span>
                </div>
              </div>
              <div class="mp-topic">
                <span class="mp-topic-dot"></span>
                <div>
                  <span class="mp-topic-name">Portfolio &amp; Career Prep</span>
                  <span class="mp-topic-note">Resume workshops, mock interviews, job application support</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="mp-section">
          <h2 class="mp-section-title">What you get</h2>
          <div class="mp-section-body">
            <p>
              This isn't a passive learning program. Mentees are expected to build, 
              ship, and show up — and in return, they get real support to do that.
            </p>
            <div class="mp-benefits">
              <div class="mp-benefit">
                <span class="mp-benefit-label">Monthly data stipends</span>
                <p>So connectivity isn't what stops you from showing up.</p>
              </div>
              <div class="mp-benefit">
                <span class="mp-benefit-label">Webinars &amp; 1:1 sessions</span>
                <p>Group learning plus direct access to mentors for focused feedback.</p>
              </div>
              <div class="mp-benefit">
                <span class="mp-benefit-label">Hands-on projects</span>
                <p>Real-world scenarios you can add to your portfolio and talk about in interviews.</p>
              </div>
              <div class="mp-benefit">
                <span class="mp-benefit-label">Resume &amp; job prep</span>
                <p>Workshops, reviews, and application support before the program ends.</p>
              </div>
              <div class="mp-benefit">
                <span class="mp-benefit-label">Free learning resources</span>
                <p>Curated materials, tools, and access to platforms relevant to your track.</p>
              </div>
              <div class="mp-benefit">
                <span class="mp-benefit-label">Certification</span>
                <p>A NexaScale certificate upon successful completion of the program.</p>
              </div>
            </div>
          </div>
        </div>

        <div class="mp-section mp-section--cta">
          <h2 class="mp-section-title mp-section-title--light">Who can join?</h2>
          <div class="mp-section-body mp-section-body--light">
            <p>
              This program is designed for entry-level engineers — people who already 
              know the basics and are ready to go deeper. If you're brand new to tech 
              and still learning fundamentals, the Study Groups are a better starting 
              point and you can come back for mentorship once you've built your foundation.
            </p>
            <p>
              We run cohorts periodically. The best way to know when the next one opens 
              is to join our community and stay in the loop — announcements go there first.
            </p>
            <br />
            <a class="mp-btn mp-btn--yellow" href="https://forms.gle/nivXcK3MwJ8ZeFRp7" target="_blank">
              Stay in touch →
            </a>
          </div>
        </div>

      </div>
    </section>

  </div>
</template>

<style scoped>
.mp-page {
  min-height: var(--pageheight, 100vh);
  margin-top: 80px;
  text-align: left;
}
.mp-page * { box-sizing: border-box; }
.mp-page p, .mp-page h1, .mp-page h2 { text-align: left; }

/* HERO */
.mp-hero {
  background: #191616;
  padding: 80px 7%;
}
.mp-hero-inner {
  max-width: 1200px;
  margin: 0 auto;
  display: grid;
  grid-template-columns: 1fr 420px;
  gap: 72px;
  align-items: center;
}
.mp-eyebrow {
  font-size: 0.65rem;
  font-weight: 700;
  letter-spacing: 0.16em;
  text-transform: uppercase;
  color: #FFB812;
  display: block;
  margin-bottom: 16px;
}
.mp-h1 {
  font-size: clamp(2.2rem, 5vw, 4rem);
  font-weight: 800;
  letter-spacing: -0.03em;
  line-height: 1.05;
  color: #ffffff;
  margin: 0 0 20px;
  word-break: break-word;
}
.mp-hero-body {
  font-size: 0.97rem;
  line-height: 1.8;
  color: rgba(255,255,255,0.55);
  margin: 0 0 32px;
  max-width: 520px;
}
.mp-btn {
  display: inline-block;
  background: #FFB812;
  color: #191616;
  font-weight: 700;
  font-size: 0.9rem;
  padding: 12px 26px;
  border-radius: 8px;
  text-decoration: none;
  transition: background 0.2s;
}
.mp-btn:hover { background: #FF9914; }
.mp-btn--yellow { background: #FFB812; color: #191616; }

.mp-hero-image {
  border-radius: 16px;
  overflow: hidden;
}
.mp-hero-image img {
  width: 100%;
  display: block;
  border-radius: 16px;
  object-fit: cover;
  object-position: center top;
  max-height: 480px;
}

/* CONTENT */
.mp-content { background: #ffffff; }
.mp-content-inner {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 7%;
}

.mp-section {
  padding: 72px 0;
  border-bottom: 1px solid #e8e4e0;
  display: grid;
  grid-template-columns: 260px minmax(0, 1fr);
  gap: 60px;
  align-items: start;
}
.mp-section:last-child { border-bottom: none; }
.mp-section--cta {
  background: #191616;
  margin: 0 -7%;
  padding: 72px 7%;
  border-bottom: none;
}

.mp-section-title {
  font-size: 1.2rem;
  font-weight: 800;
  letter-spacing: -0.01em;
  color: #191616;
  margin: 0;
  position: sticky;
  top: 100px;
  word-break: break-word;
}
.mp-section-title--light { color: #ffffff; }

.mp-section-body p {
  font-size: 0.97rem;
  line-height: 1.8;
  color: #555250;
  margin: 0 0 16px;
}
.mp-section-body p:last-of-type { margin-bottom: 0; }
.mp-section-body--light p { color: rgba(255,255,255,0.6); }

/* TOPICS */
.mp-topics {
  display: flex;
  flex-direction: column;
  border-top: 1px solid #e8e4e0;
  margin-top: 20px;
}
.mp-topic {
  display: flex;
  gap: 16px;
  align-items: flex-start;
  padding: 16px 0;
  border-bottom: 1px solid #e8e4e0;
}
.mp-topic:last-child { border-bottom: none; }
.mp-topic-dot {
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: #FFB812;
  flex-shrink: 0;
  margin-top: 6px;
}
.mp-topic-name {
  display: block;
  font-size: 0.95rem;
  font-weight: 700;
  color: #191616;
  margin-bottom: 4px;
}
.mp-topic-note {
  display: block;
  font-size: 0.8rem;
  color: #aaa8a4;
  line-height: 1.5;
}

/* BENEFITS */
.mp-benefits {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0;
  border-top: 1px solid #e8e4e0;
  border-left: 1px solid #e8e4e0;
  margin-top: 24px;
}
.mp-benefit {
  padding: 24px;
  border-right: 1px solid #e8e4e0;
  border-bottom: 1px solid #e8e4e0;
}
.mp-benefit-label {
  display: block;
  font-size: 0.88rem;
  font-weight: 700;
  color: #191616;
  margin-bottom: 6px;
}
.mp-benefit p {
  font-size: 0.83rem !important;
  line-height: 1.6 !important;
  color: #888480 !important;
  margin: 0 !important;
}

/* RESPONSIVE */
@media (max-width: 900px) {
  .mp-hero-inner { grid-template-columns: 1fr; gap: 40px; }
  .mp-hero-image { order: -1; }
  .mp-hero-image img { max-height: 280px; }
  .mp-section { grid-template-columns: 1fr; gap: 20px; padding: 52px 0; }
  .mp-section--cta { padding: 52px 5%; margin: 0 -5%; }
  .mp-section-title { position: static; }
  .mp-hero { padding: 60px 5%; }
  .mp-content-inner { padding: 0 5%; }
  .mp-benefits { grid-template-columns: 1fr; }
}
</style>